<template>
  <v-app>
    <v-app-bar v-if="$route.name != 'Login'" app elevation="2" color="white">
      <div class="d-flex align-center">
        <v-app-bar-nav-icon 
          @click.stop="drawer = !drawer"
          class="grey--text text--darken-2"
        ></v-app-bar-nav-icon>

        <v-img 
          alt="Calcutta Records" 
          class="shrink mr-3" 
          contain
          src="https://calcuttarecords.com/wp-content/uploads/2023/05/cropped-cropped-cropped-cropped-CalcuttaRecordCompany-2-01-150x150-1-1.webp"
          transition="scale-transition" 
          width="36" 
          height="36"
        />
        <span class="app-title font-weight-medium">CalcuttaRecords</span>
        <v-chip 
          small 
          outlined 
          color="primary" 
          class="ml-2 text-caption font-weight-medium version-chip"
        >
          v2.0
        </v-chip>
      </div>

      <v-spacer></v-spacer>
      
      <v-btn 
        @click="refresh" 
        text 
        class="mr-2 refresh-btn" 
        color="primary"
      >
        <v-icon left size="18">mdi-refresh</v-icon>
        <span class="d-none d-sm-inline">Refresh</span>
      </v-btn>
      
      <v-menu offset-y transition="slide-y-transition" left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn 
            icon 
            v-bind="attrs" 
            v-on="on"
            class="ml-2"
          >
            <v-avatar size="32" color="primary" class="white--text">
              <span class="text-subtitle-2">{{ user.name ? user.name.charAt(0).toUpperCase() : 'U' }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <v-list dense nav>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title class="text-subtitle-2 font-weight-medium">{{ user.name || 'User' }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption">{{ user.role || 'Staff' }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item link @click="logout">
            <v-list-item-icon class="mr-2">
              <v-icon size="18">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Logout</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-navigation-drawer 
      v-if="$route.name != 'Login'" 
      app 
      v-model="drawer"
      class="navigation-drawer"
      width="260"
    >
      <v-sheet class="pa-4 primary" dark>
        <v-row class="mb-1">
          <v-col cols="12" class="px-2">
            <div class="d-flex align-center">
              <v-avatar size="42" class="mr-4">
                <v-img 
                  src="https://calcuttarecords.com/wp-content/uploads/2023/05/cropped-cropped-cropped-cropped-CalcuttaRecordCompany-2-01-150x150-1-1.webp"
                  alt="Calcutta Records"
                ></v-img>
              </v-avatar>
              <div>
                <div class="text-subtitle-1 font-weight-medium">Calcutta Records</div>
                <div class="text-caption">Management System</div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-sheet>
      
      <v-divider></v-divider>
      
      <v-list nav dense class="py-0">
        <v-list-item-group color="primary" v-model="activeMenuItem">
          <!-- Inventory section -->
          <v-list-item v-if="user.role == 'manager' || user.role == 'agent'" link to="/home" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-archive-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Inventory</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- For shipping vendor only -->
          <v-list-item v-if="user.role == 'shipping'" link to="/dashboard" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-view-dashboard-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Dashboard</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Purchase section -->
          <v-list-item v-if="user.role == 'manager'" link to="/purchase" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-cart-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Purchase</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item v-if="user.role == 'manager'" link to="/external-order-tracker" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-truck-fast-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>External Order Tracker</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Packaging section -->
          <v-list-item v-if="user.role == 'manager' || user.role == 'agent'" link to="/packaging" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-package-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Packaging</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Supplier Packaging section -->
          <v-list-item v-if="user.role == 'manager' || user.role == 'agent'" link to="/supplier-packaging" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-package-variant-closed</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Supplier Packaging</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Shipping section -->
          <v-list-item v-if="user.role == 'manager'" link to="/shipping" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-truck-delivery-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Shipping</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Publishing section -->
          <v-list-item v-if="user.role == 'manager' || user.role == 'agent'" link to="/publishing" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-publish</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Publishing</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Remittance section -->
          <v-list-item v-if="user.role == 'manager' || user.role == 'shipping'" link to="/remittance" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-cash-multiple</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Remittance</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Reports section -->
          <v-list-item v-if="user.role == 'manager'" link to="/reports" class="menu-item">
            <v-list-item-icon>
              <v-icon>mdi-chart-box-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Reports</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
      
      <template v-slot:append>
        <div class="pa-2">
          <v-divider class="mb-2"></v-divider>
          <v-list-item link @click="logout" class="menu-item">
            <v-list-item-icon>
              <v-icon color="grey darken-1">mdi-logout-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="grey--text text--darken-1">Logout</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </div>
      </template>


    </v-navigation-drawer>

    <v-main>
      <router-view />
    </v-main>
  </v-app>
</template>

<script>
import { mapState } from 'vuex';
export default {
  name: 'App',

  data: () => ({
    drawer: false,
    activeMenuItem: 0,
  }),
  computed: {
    ...mapState(['user']),
  },
  methods: {
    refresh() {
      window.location.reload()
    },

    async logout() {
      await this.$store.dispatch('logout');
      this.$router.go();
    }

  }
};
</script>

<style scoped>
.app-title {
  font-size: 1.25rem;
  letter-spacing: 0.5px;
  color: #424242;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .app-title {
    font-size: 1rem;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media (max-width: 400px) {
  .app-title {
    max-width: 100px;
  }
}

.version-chip {
  height: 20px;
}

.navigation-drawer {
  border-right: 1px solid rgba(0, 0, 0, 0.05);
}

.menu-item {
  border-radius: 4px;
  margin: 4px 8px;
}

.v-list-item--active.menu-item {
  margin-left: 0;
  margin-right: 8px;
  border-left: 4px solid var(--v-primary-base);
}

.refresh-btn {
  min-width: 0 !important;
  padding: 0 8px !important;
}

@media (max-width: 600px) {
  .refresh-btn {
    margin-right: 0 !important;
  }
}
</style>
